import { ref } from 'vue';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

// ponytail: AppRoot scope — only files in this app's isolated AppRoot folder
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const MANIFEST_NAME = 'punchlog-manifest.json';
const FOLDER_NAME = 'PunchLog Backups';
const SCOPES = ['Files.ReadWrite.AppFolder'];

// ponytail: singleton MSAL instance — one per page lifecycle
let msalInstance = null;

function getMsal() {
  if (msalInstance) return msalInstance;
  msalInstance = new PublicClientApplication({
    auth: {
      clientId: import.meta.env.VITE_AZURE_CLIENT_ID ?? 'REPLACE_WITH_AZURE_CLIENT_ID',
      redirectUri: globalThis.location?.origin ?? 'https://punchlog.app',
    },
    cache: { cacheLocation: 'localStorage' },
  });
  return msalInstance;
}

async function getAccessToken() {
  const msal = getMsal();
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  const request = { scopes: SCOPES, account: accounts[0] };
  try {
    const res = await msal.acquireTokenSilent(request);
    return res.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      const res = await msal.acquireTokenPopup(request);
      return res.accessToken;
    }
    throw e;
  }
}

async function graphRequest(token, path, opts = {}) {
  const res = await fetch(`${GRAPH_BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) throw new Error(`Graph ${opts.method ?? 'GET'} ${path} → ${res.status}`);
  return res.json();
}

// AppRoot items are addressed as /me/drive/special/approot:/path:/content
async function ensureFolder(token) {
  // Graph AppRoot folders create on first PUT — no explicit mkdir needed
  return FOLDER_NAME;
}

async function downloadFile(token, name) {
  try {
    const res = await fetch(
      `${GRAPH_BASE}/me/drive/special/approot:/${FOLDER_NAME}/${name}:/content`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`downloadFile ${name} → ${res.status}`);
    return res.json();
  } catch {
    return null;
  }
}

async function downloadManifest(token) {
  return downloadFile(token, MANIFEST_NAME);
}

async function uploadFile(token, name, content) {
  const res = await fetch(
    `${GRAPH_BASE}/me/drive/special/approot:/${FOLDER_NAME}/${name}:/content`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(content),
    }
  );
  if (!res.ok) throw new Error(`uploadFile ${name} → ${res.status}`);
}

// LWW re-used from Drive composable pattern
export function resolveOneDriveManifest(local, remote) {
  if (!local) return remote;
  if (!remote) return local;
  return new Date(local.updatedAt) >= new Date(remote.updatedAt) ? local : remote;
}

export async function oneDriveSignIn() {
  const msal = getMsal();
  await msal.initialize();
  const res = await msal.loginPopup({ scopes: SCOPES });
  return res;
}

export async function oneDriveSignOut() {
  const msal = getMsal();
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (accounts[0]) await msal.logoutPopup({ account: accounts[0] });
}

const lastSyncAt = ref(null);
const syncError  = ref(null);
const isSyncing  = ref(false);

export function useOneDriveSync() {
  async function uploadData(name, data) {
    const token = await getAccessToken();
    return uploadFile(token, name, data);
  }

  async function downloadData(name) {
    const token = await getAccessToken();
    return downloadFile(token, name);
  }

  async function syncBundle({ bundleId, bundleJson }) {
    isSyncing.value = true;
    syncError.value = null;
    try {
      const token = await getAccessToken();
      await ensureFolder(token);
      await uploadFile(token, `bundle-${bundleId}.json`, bundleJson);

      const remote = await downloadManifest(token);
      const now    = new Date().toISOString();
      const local  = { updatedAt: now, bundles: [...(remote?.bundles ?? []), bundleId] };
      const winner = resolveOneDriveManifest(local, remote);
      if (!winner.bundles.includes(bundleId)) winner.bundles.push(bundleId);
      winner.updatedAt = now;
      await uploadFile(token, MANIFEST_NAME, winner);

      lastSyncAt.value = now;
    } catch (e) {
      syncError.value = e.message;
      throw e;
    } finally {
      isSyncing.value = false;
    }
  }

  return { signIn: oneDriveSignIn, signOut: oneDriveSignOut, syncBundle, uploadData, downloadData, lastSyncAt, syncError, isSyncing };
}
